import { IconButton, Link, SxProps } from "@mui/material";
import { StyledButton } from "./StyledButton";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";
import { ReactNode } from "react";

export interface UIButtonProps {
    variant?:
        | "text"
        | "contained"
        | "dark"
        | "outlined"
        | "light"
        | "icon"
        | "link"
        | "link_underlined"
        | "link_underlined_hover";
    size?: "small" | "medium" | "large";
    disabled?: boolean;
    onClick?: () => void;
    startIcon?: JSX.Element;
    padding?: string;
    paddingTop?: string;
    paddingBottom?: string;
    paddingLeft?: string;
    paddingRight?: string;
    sx?: SxProps;
    children?: ReactNode;
    color?: string;
    tabIndex?: number;
    fullWidth?: boolean;
    width?: string;
}

export function UIButton(props: UIButtonProps) {
    let width = props.width;

    if (props.fullWidth) width = "100%";

    return (
        <>
            {(props.variant === "contained" || props.variant === "dark" || !props.variant) && (
                <StyledButton
                    sx={{ ...props.sx, width: width, background: props.color ?? UIStyleConstants.BLUE_COLOR }}
                    variant={"contained"}
                    size={props.size ?? "medium"}
                    disabled={props.disabled}
                    onClick={props.onClick}
                    startIcon={props.startIcon}
                    tabIndex={props.tabIndex}
                    style={{
                        padding: props.padding,
                        paddingLeft: props.paddingLeft,
                        paddingRight: props.paddingRight,
                        paddingBottom: props.paddingBottom,
                        paddingTop: props.paddingTop,
                    }}
                >
                    {props.children}
                </StyledButton>
            )}

            {props.variant === "text" && (
                <StyledButton
                    startIcon={props.startIcon}
                    variant={"contained"}
                    size={props.size ?? "small"}
                    disabled={props.disabled}
                    onClick={props.onClick}
                    tabIndex={props.tabIndex}
                    style={{
                        boxShadow: "unset",
                        border: "unset",
                        background: "transparent",
                        padding: props.padding,
                        paddingLeft: props.paddingLeft,
                        paddingRight: props.paddingRight,
                        paddingBottom: props.paddingBottom,
                        paddingTop: props.paddingTop,
                        color: props.color,
                    }}
                    sx={{ width: width, ...props.sx }}
                >
                    {props.children}
                </StyledButton>
            )}

            {props.variant === "outlined" && (
                <StyledButton
                    startIcon={props.startIcon}
                    variant={"outlined"}
                    size={props.size ?? "medium"}
                    disabled={props.disabled}
                    onClick={props.onClick}
                    tabIndex={props.tabIndex}
                    style={{
                        padding: props.padding,
                        paddingLeft: props.paddingLeft,
                        paddingRight: props.paddingRight,
                        paddingBottom: props.paddingBottom,
                        paddingTop: props.paddingTop,
                    }}
                    sx={{
                        width: width,
                        background: props.color ?? UIStyleConstants.WHITE_COLOR,
                        borderColor: UIStyleConstants.BLUE_COLOR,
                        color: UIStyleConstants.BLUE_COLOR,
                        ...props.sx,
                    }}
                >
                    {props.children}
                </StyledButton>
            )}

            {props.variant === "light" && (
                <StyledButton
                    startIcon={props.startIcon}
                    variant={"contained"}
                    size={props.size ?? "medium"}
                    disabled={props.disabled}
                    onClick={props.onClick}
                    tabIndex={props.tabIndex}
                    style={{
                        padding: props.padding,
                        paddingLeft: props.paddingLeft,
                        paddingRight: props.paddingRight,
                        paddingBottom: props.paddingBottom,
                        paddingTop: props.paddingTop,
                        background: UIStyleConstants.GRAY_LIGHT_COLOR,
                        color: UIStyleConstants.BLUE_COLOR,
                    }}
                    sx={{ width: width, ...props.sx }}
                >
                    {props.children}
                </StyledButton>
            )}

            {props.variant === "link" && (
                <Link
                    style={{
                        padding: props.padding,
                        paddingLeft: props.paddingLeft,
                        paddingRight: props.paddingRight,
                        paddingBottom: props.paddingBottom,
                        paddingTop: props.paddingTop,
                    }}
                    underline={"none"}
                    onClick={props.disabled ? undefined : props.onClick}
                    tabIndex={props.tabIndex}
                    sx={props.sx}
                >
                    {props.children}
                </Link>
            )}

            {props.variant === "link_underlined" && (
                <Link
                    style={{
                        padding: props.padding,
                        paddingLeft: props.paddingLeft,
                        paddingRight: props.paddingRight,
                        paddingBottom: props.paddingBottom,
                        paddingTop: props.paddingTop,
                    }}
                    underline={"always"}
                    onClick={props.disabled ? undefined : props.onClick}
                    tabIndex={props.tabIndex}
                    sx={{ width: width, ...props.sx }}
                >
                    {props.children}
                </Link>
            )}

            {props.variant === "link_underlined_hover" && (
                <Link
                    style={{
                        padding: props.padding,
                        paddingLeft: props.paddingLeft,
                        paddingRight: props.paddingRight,
                        paddingBottom: props.paddingBottom,
                        paddingTop: props.paddingTop,
                    }}
                    tabIndex={props.tabIndex}
                    underline={"hover"}
                    onClick={props.disabled ? undefined : props.onClick}
                    sx={{ width: width, ...props.sx }}
                >
                    {props.children}
                </Link>
            )}

            {props.variant === "icon" && (
                <IconButton
                    style={{
                        padding: props.padding,
                        paddingLeft: props.paddingLeft,
                        paddingRight: props.paddingRight,
                        paddingBottom: props.paddingBottom,
                        paddingTop: props.paddingTop,
                    }}
                    size={props.size ?? "medium"}
                    tabIndex={props.tabIndex}
                    disabled={props.disabled}
                    onClick={props.onClick}
                    sx={{ width: width, ...props.sx }}
                >
                    {props.children}
                </IconButton>
            )}
        </>
    );
}
